import type { HoldemOptions } from '@tv-casino/shared';

/**
 * What the chip tray is sizing on a Hold'em decision.
 *
 * The server wants a street total ("raise to"). The tray, like every other
 * game, stages the chips leaving the stack right now. `streetIn` is already
 * out this street, so the two add up to the number the table accepts.
 */
export interface HoldemChipStake {
  streetIn: number;
  /** Smallest pile the table will take. */
  addMin: number;
  /** Largest pile the table will take — the legal max, not the whole stack. */
  addMax: number;
  /** The tray can build a pile the table will take. */
  canSize: boolean;
}

export function holdemChipStake(options: HoldemOptions, chips: number): HoldemChipStake {
  const behind = Math.max(0, Math.floor(chips));
  const streetIn = Math.max(0, options.allInTo - behind);
  const addMin = options.minRaiseTo - streetIn;
  const addMax = options.maxRaiseTo - streetIn;
  return {
    streetIn,
    addMin,
    addMax,
    canSize: addMin >= 1 && addMax >= addMin,
  };
}

export interface HoldemPreset {
  label: string;
  /** Street total this preset sizes to. */
  to: number;
  /** Chips the tray should hold to reach `to`. */
  add: number;
}

/**
 * Min, half pot, pot, and all-in. Pot fractions snap to the big blind and
 * stay inside the legal window, same sizes the slider used to offer.
 */
export function holdemPresets(
  options: HoldemOptions,
  stake: HoldemChipStake,
  potTotal: number,
  currentBet: number,
  bigBlind: number,
): HoldemPreset[] {
  const blind = Math.max(1, Math.floor(bigBlind));
  const fit = (value: number) => {
    const stepped = Math.round(value / blind) * blind;
    return Math.min(options.maxRaiseTo, Math.max(options.minRaiseTo, stepped));
  };
  const rows = [
    { label: 'Min', to: options.minRaiseTo },
    { label: '½ Pot', to: fit(currentBet + Math.floor((potTotal + options.callAmount) / 2)) },
    { label: 'Pot', to: fit(currentBet + potTotal + options.callAmount) },
    { label: 'All in', to: options.maxRaiseTo },
  ];
  return rows.map((row) => ({ ...row, add: row.to - stake.streetIn }));
}

/** Button copy for a pile. An empty circle stays a verb; a shove says so. */
export function holdemStakeLabel(
  isBet: boolean,
  streetIn: number,
  added: number,
  allInTo: number,
): string {
  if (added <= 0) return isBet ? 'Bet' : 'Raise';
  const to = streetIn + added;
  const text = to.toLocaleString();
  if (allInTo > 0 && to >= allInTo) return `All in ${text}`;
  return `${isBet ? 'Bet' : 'Raise to'} ${text}`;
}
